import { User } from '@auth0/auth0-react';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import { Paths } from '../constants/paths';
import { getAppRouteParams } from '../utils/app';

import { usePrevious } from '@controlrooms/hooks';

interface ContextProps {
  tenant: number;
  updateTenant: (tenantId: number, params?: URLSearchParams, user?: User) => void;
}

const stub = (): never => {
  throw new Error('You forgot to wrap your component in <TenantContextProvider>.');
};

const defaultState = {
  tenant: 0,
  updateTenant: stub,
};

const TenantContext = createContext<ContextProps>(defaultState);

export const useTenant = () => useContext(TenantContext);

const TenantContextProvider: React.FC = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Initialize tenant from URL
  const [tenant, setTenant] = useState<number>(() => {
    const match = getAppRouteParams(location.pathname);
    const tenantId = match?.params?.tenantId;
    return tenantId && /\d+/.test(tenantId) ? Number(tenantId) : 0;
  });

  const previousTenant = usePrevious(tenant);

  // SINGLE CONSOLIDATED EFFECT
  useEffect(() => {
    const match = getAppRouteParams(location.pathname);

    if (!match) return;

    const tenantIdStr = match.params.tenantId as string;

    // Validate format
    if (!/\d+/.test(tenantIdStr)) {
      navigate(Paths.NOT_FOUND);
      return;
    }

    const newTenantId = Number(tenantIdStr);

    // Check if tenant actually changed
    if (tenant === newTenantId) {
      // Same tenant - just validate session storage
      const stored = sessionStorage.getItem('tenant');
      if (stored !== tenantIdStr) {
        sessionStorage.setItem('tenant', tenantIdStr);
      }
      return;
    }

    // Tenant changed - clear old data
    if (previousTenant !== undefined && previousTenant !== newTenantId) {
      // Clear session storage
      sessionStorage.clear();

      // Targeted cache removal instead of full clear
      queryClient.removeQueries({
        predicate: (query) => {
          // Only remove queries that are tenant-specific
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key.some(
              (k) =>
                typeof k === 'object' && k !== null && 'tenant' in k && k.tenant === previousTenant,
            )
          );
        },
      });
    }

    // Update tenant
    setTenant(newTenantId);
    sessionStorage.setItem('tenant', String(newTenantId));
  }, [location.pathname, navigate, queryClient, tenant, previousTenant]);

  const updateTenant = useCallback(
    (tenantId: number, params?: URLSearchParams) => {
      if (tenant === tenantId) return;
      if (!params) {
        navigate(generatePath(Paths.VIEWS, { tenantId: tenantId.toString() }));
      } else {
        const current = getAppRouteParams(location.pathname);
        current &&
          navigate(
            `${generatePath(current.pattern.path, {
              ...current.params,
              tenantId: tenantId.toString(),
            })}${params ? `?${params}` : ''}`,
          );
      }
    },
    [location.pathname, navigate, tenant],
  );

  return (
    <TenantContext.Provider value={{ tenant, updateTenant }}>{children}</TenantContext.Provider>
  );
};

export default TenantContextProvider;
